import React from "react";
import { Loader2 } from "lucide-react";

import { Checkbox } from "components/ui/Checkbox";
import { DataLoadError } from "components/ui/DataLoadError";
import { DataTableCell } from "components/ui/DataTable/components/DataTableCell/DataTableCell";
import { DataTableRowActions } from "components/ui/DataTable/components/DataTableRowActions/DataTableRowActions";

import styles from "./DataTableBody.module.css";

export const DataTableBody = ({
  columns,
  data,
  getRowId,
  selectable,
  isRowSelectable,
  rowActions,
  gridColumns,
  isSelected,
  toggleSelection,
  onRowAction,
  onRowClick,
  isLoading,
  isError,
  errorLabel,
  onRetry,
  emptyIllustration,
  emptyTitle,
  emptyText,
  loadingTitle,
  loadingText,
  getRowClassName,
  getRowTestId,
  rowActionsLabel,
}) => {
  // A failed fetch must never fall through to the empty state — "no items
  // found" is a statement about the data, and we do not have the data.
  if (isError && data.length === 0) {
    return (
      <DataLoadError variant="block" label={errorLabel} onRetry={onRetry} />
    );
  }

  if (isLoading) {
    return (
      <div className={styles.emptyState}>
        <Loader2 className={styles.loadingSpinner} size={24} />
        <p className={styles.emptyStateTitle}>{loadingTitle}</p>
        <p className={styles.emptyStateText}>{loadingText}</p>
      </div>
    );
  }

  if (data.length === 0) {
    // Rendered inside the table, never in place of it — the column headers are
    // part of what tells the user what is empty.
    return (
      <div
        className={`${styles.emptyState} ${emptyIllustration ? styles.emptyStateWithIllustration : ""}`}
      >
        {!!emptyIllustration && (
          <img
            src={emptyIllustration}
            alt=""
            aria-hidden="true"
            className={styles.emptyStateIllustration}
          />
        )}
        <div className={styles.emptyStateCopy}>
          <p className={styles.emptyStateTitle}>{emptyTitle}</p>
          <p className={styles.emptyStateText}>{emptyText}</p>
        </div>
      </div>
    );
  }

  return (
    <ul className={styles.tableBody}>
      {data.map((row, index) => {
        const rowId = getRowId(row);
        const selected = isSelected(rowId);
        const isSelectableRow = isRowSelectable?.(row) !== false;
        const rowVariant = getRowClassName?.(row);
        const variantClass = rowVariant === "warning" ? styles.rowWarning : rowVariant === "error" ? styles.rowError : rowVariant === "disabled" ? styles.rowDisabled : rowVariant === "cancelled" ? styles.rowCancelled : rowVariant === "active" ? styles.rowActive : rowVariant === "doesNotCount" ? styles.rowDoesNotCount : (rowVariant ?? "");
        return (
          <li
            key={rowId}
            className={`${styles.tableRow}${variantClass ? ` ${variantClass}` : ""}${onRowClick ? ` ${styles.tableRowClickable}` : ""}`}
            style={{ gridTemplateColumns: gridColumns }}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            {...(getRowTestId && {
              "data-testid": getRowTestId(row),
            })}
          >
            {selectable && !isSelectableRow && (
              /*
               * The row keeps its place in the grid — an empty cell, not a missing one — so a row
               * the bulk actions cannot act on still lines up with the rows that can.
               */
              <div className={styles.checkboxCell} />
            )}
            {selectable && isSelectableRow && (
              /*
               * The click is stopped on the cell, not only inside the change handler.
               * `stopPropagation` on `change` does not stop the `click` that produced it,
               * so ticking a row also fired the row's own onClick — in Letters that meant
               * selecting a letter opened its editor.
               */
              <div
                className={styles.checkboxCell}
                onClick={(event) => event.stopPropagation()}
              >
                <Checkbox
                  checked={selected}
                  handleChange={(e) => {
                    e.stopPropagation();
                    toggleSelection(rowId, row, e.target.checked);
                  }}
                  className={styles.rowCheckbox}
                />
              </div>
            )}
            {columns.map((col) => {
              const content = col.render
                ? col.render(row, index)
                : row[col.id] ?? "-";
              const fullText =
                typeof content === "string"
                  ? content
                  : col.getTooltipText?.(row);
              const isClickableWhen =
                !col.clickableWhen || col.clickableWhen(row);
              const clickable =
                !!col.onClick && !col.clickableSubtle && isClickableWhen;
              const clickableSubtle =
                !!col.onClick && !!col.clickableSubtle && isClickableWhen;
              return (
                <DataTableCell
                  key={col.id}
                  content={content}
                  fullText={fullText}
                  clickable={clickable}
                  clickableSubtle={clickableSubtle}
                  wrap={col.wrap}
                  noDim={col.noDim}
                  onClick={
                    (clickable || clickableSubtle)
                      ? (e) => {
                          e?.stopPropagation?.();
                          col.onClick?.(row);
                        }
                      : undefined
                  }
                  data-testid={col.testId}
                />
              );
            })}
            {rowActions?.length > 0 && (
              <DataTableRowActions
                actions={rowActions}
                row={row}
                onAction={onRowAction}
                ariaLabel={rowActionsLabel}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};
